import { NgIf, NgFor, AsyncPipe } from '@angular/common';
import { Component, OnDestroy, inject } from '@angular/core';
import { NotificationEmptyComponent } from '@next/core-lib/components';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { DataSerie, DataSeries, DataSeriesParameter } from '@next/core-lib/models';
import { SummaryCardKPIChartItem } from '@next/core-lib/summary-card-kpi-chart';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { map, skipWhile } from 'rxjs/operators';
import {
  BenchmarkSettings,
  BenchmarkTypes,
  DataViewType,
  FarmerInsightsModuleState,
  FlockDataParameter,
} from 'src/app/farmer-insights/models/farmer-insights.model';
import { FlockDataService } from 'src/app/farmer-insights/services/flock-data.service';
import {
  getBenchmarkCustomerAveragSelectedPeriodData,
  getBenchmarkLastWeeksData,
  getBenchmarkSelectedPeriodData,
  getBenchmarkSettings,
  getSelectedDataViewType,
  getTimeSeries,
} from 'src/app/farmer-insights/store/selectors/farmer-insights.selectors';
import { MIN_FLOCKS_FOR_BENCHMARK } from 'src/app/shared/constants';
import { TenantAwareTranslationService } from 'src/app/shared/services/tenant-aware-translation.service';

import { SummaryCardKPIChartComponent } from '../../../../shared/components/summary-card-kpi-chart/src/summary-card-kpi-chart.component';
import { BenchmarkData } from '../../../models/farmer-insights.model';
import {
  getBenchmarkTimeSeries,
  getSelectedKpis,
} from '../../../store/selectors/farmer-insights.selectors';

@Component({
  selector: 'app-data-view-graph-simple',
  templateUrl: './data-view-graph-simple.component.html',
  styleUrls: ['./data-view-graph-simple.component.scss'],
  imports: [
    NgIf,
    NgFor,
    SummaryCardKPIChartComponent,
    NotificationEmptyComponent,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class DataViewGraphSimpleComponent implements OnDestroy {
  private readonly store = inject<Store<FarmerInsightsModuleState>>(Store);
  private readonly localeService = inject(LocaleService);
  private readonly tenantTranslation = inject(TenantAwareTranslationService);
  private readonly flockDataService = inject(FlockDataService);

  public timeSeries$: Observable<DataSeries<number>> = this.store.select(getTimeSeries);
  public kpis$: Observable<FlockDataParameter[]> = this.store.select(getSelectedKpis);
  public selectedDataViewType$: Observable<DataViewType> =
    this.store.select(getSelectedDataViewType);
  public benchmarkTimeSeries$: Observable<DataSeries<number>> =
    this.store.select(getBenchmarkTimeSeries);
  public benchmarkSelectedPeriodData$: Observable<BenchmarkData> = this.store.select(
    getBenchmarkSelectedPeriodData,
  );
  public benchmarkLastWeeksData$: Observable<BenchmarkData> =
    this.store.select(getBenchmarkLastWeeksData);
  public benchmarkCustomerAverageSelectedPeriodData$: Observable<BenchmarkData> = this.store.select(
    getBenchmarkCustomerAveragSelectedPeriodData,
  );

  kpiItems: { [key: string]: SummaryCardKPIChartItem[] } = {};
  kpiSummaryLabels: { [key: string]: string } = {};
  kpiSummaryValues: { [key: string]: number } = {};
  kpiSummaryUnits: { [key: string]: string } = {};
  kpiSummaryPrefixUnits: { [key: string]: boolean } = {};
  kpiSummaryDecimals: { [key: string]: number } = {};
  kpiSummarySubValues: { [key: string]: number } = {};
  subTimeSeries: Subscription;
  hasAvailableData = false;

  public benchmarkSettings$: Observable<BenchmarkSettings> =
    this.store.select(getBenchmarkSettings);
  public benchmarkSettings: BenchmarkSettings = null;

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {
    this.subTimeSeries = combineLatest([
      this.timeSeries$,
      this.benchmarkTimeSeries$,
      this.benchmarkSelectedPeriodData$,
      this.benchmarkLastWeeksData$,
      this.benchmarkCustomerAverageSelectedPeriodData$,
      this.selectedDataViewType$,
      this.benchmarkSettings$,
    ]).subscribe(
      ([
        timeSeries,
        benchmarks,
        selectedPeriodData,
        lastWeeksData,
        customerAverageSelectedPeriodData,
        dataViewType,
        benchmarkSettings,
      ]: [
        DataSeries<number>,
        DataSeries<number>,
        BenchmarkData,
        BenchmarkData,
        BenchmarkData,
        DataViewType,
        BenchmarkSettings,
      ]) => {
        this.benchmarkSettings = benchmarkSettings;
        const activeBenchmarkData = this.getBenchmarkData(
          selectedPeriodData,
          lastWeeksData,
          customerAverageSelectedPeriodData,
        );

        if (timeSeries === null) {
          this.hasAvailableData = false;
          return;
        } else {
          this.hasAvailableData = true;
        }
        // Process KPI's.
        const kpis = Object.keys(timeSeries.series);

        this.hasAvailableData = timeSeries?.series[kpis[0]]?.length > 0;

        if (this.hasAvailableData) {
          kpis.forEach((kpi) => {
            this.kpiItems[kpi] = [];
            // Only use the series of the detail level. Don't show the totals in the simple graphs.
            let series = [...timeSeries.series[kpi]].filter((ts) => ts.name !== 'Total');
            const total = [...timeSeries.series[kpi]].filter((ts) => ts.name === 'Total');

            // Calculate total for this KPI.
            const kpiMeta = timeSeries.metaData.parameters.find((c) => c.code === kpi);

            // Get Summary label and data (all series, without slicing).
            this.getSummaryData(kpiMeta, benchmarks, activeBenchmarkData, total);

            // Sort and slice the series to the number of items defined.
            series = this.sortAndSliceSeries(dataViewType, series);

            // Process Series Data.
            this.createSeriesItems(
              kpi,
              kpiMeta,
              dataViewType,
              series,
              benchmarks,
              activeBenchmarkData,
            );
          });
        }
      },
    );
  }

  showKpi$(kpi: FlockDataParameter): Observable<boolean> {
    return this.timeSeries$.pipe(
      map((ts) => {
        if (ts !== null) {
          const kpis = Object.keys(ts.series);
          return kpis.includes(kpi);
        }
      }),
    );
  }

  ngOnDestroy() {
    this.subTimeSeries?.unsubscribe();
  }

  isValid(value: number): boolean {
    return !Number.isNaN(value) && value.toString() !== 'NaN' && !this.isInfinity(value);
  }

  isInfinity(n: number): boolean {
    return n.toString() === 'Infinity' || n === Infinity;
  }

  getMin(n: number[]): number {
    n = n.filter((x) => this.isValid(x));
    return Math.min(...n);
  }

  getMax(n: number[]): number {
    n = n.filter((x) => this.isValid(x));
    return Math.max(...n);
  }

  getBenchmarkData(
    selectedPeriodData: BenchmarkData,
    lastWeeksData: BenchmarkData,
    customerAverageSelectedPeriodData: BenchmarkData,
  ): BenchmarkData {
    switch (this.benchmarkSettings.selectedNonDetailedBenchmarkType) {
      case BenchmarkTypes.LastWeeks:
        return lastWeeksData;
      case BenchmarkTypes.SelectedPeriod:
        return selectedPeriodData;
      case BenchmarkTypes.CustomerAverageSelectedPeriod:
        return customerAverageSelectedPeriodData;
      default:
        return {
          flocksUsed: -1,
          benchmarks: [],
        };
    }
  }

  isLowerValueBetter(kpi: FlockDataParameter) {
    return this.flockDataService.isLowerValueBetterForKpi(kpi);
  }

  showDeviationColorForKpi(kpi: FlockDataParameter) {
    return this.flockDataService.showDeviationColorForKpi(kpi);
  }

  sortAndSliceSeries(
    dataViewType: DataViewType,
    series: DataSerie<number, Date>[],
  ): DataSerie<number, Date>[] {
    if (series) {
      switch (dataViewType) {
        case DataViewType.PerCycleBarn:
        case DataViewType.PerCycle:
          series = [...series.sort(this.sortTSDataByCycle)];
          break;
        case DataViewType.PerLocation:
          series = [...series.sort(this.sortTSDataByLocation)];
          break;
        case DataViewType.PerLocationBarn:
          series = [...series.sort(this.sortTSDataByBarn)];
          break;
      }
    }
    return series;
  }

  getSummaryData(
    kpiMeta: DataSeriesParameter,
    benchmarks: DataSeries<number>,
    benchmarkData: BenchmarkData,
    total: DataSerie<number, Date>[],
  ): void {
    this.kpiSummarySubValues[kpiMeta.code] = undefined;
    this.kpiSummaryLabels[kpiMeta.code] = kpiMeta.aggregationType.toString();

    const foundTotal = total.find((x) => x.name === 'Total')?.values[0]?.value;
    if (foundTotal === undefined || isNaN(foundTotal) || !isFinite(foundTotal)) {
      this.kpiSummaryValues[kpiMeta.code] = undefined;
    } else {
      this.kpiSummaryValues[kpiMeta.code] = foundTotal;
    }

    this.kpiSummaryUnits[kpiMeta.code] = this.translateUnitOfMeasure(kpiMeta.unit);
    this.kpiSummaryPrefixUnits[kpiMeta.code] =
      (kpiMeta.properties['PrefixUnit'] as unknown as boolean) === true;
    this.kpiSummaryDecimals[kpiMeta.code] = kpiMeta.numberOfDecimals;

    if (
      benchmarks?.series[kpiMeta.code] !== undefined &&
      benchmarkData !== null &&
      benchmarkData !== undefined &&
      (this.benchmarkSettings.selectedNonDetailedBenchmarkType ===
        BenchmarkTypes.CustomerAverageSelectedPeriod ||
        benchmarkData.flocksUsed >= MIN_FLOCKS_FOR_BENCHMARK)
    ) {
      this.kpiSummarySubValues[kpiMeta.code] = benchmarkData.benchmarks.find(
        (x) => x.parameter.code === kpiMeta.code,
      )?.value;
    }

    if (this.benchmarkSettings.selectedNonDetailedBenchmarkType === BenchmarkTypes.None) {
      this.kpiSummarySubValues[kpiMeta.code] = undefined;
    }
  }

  createSeriesItems(
    kpi: string,
    dataSeriesParameter: DataSeriesParameter,
    dataViewType: DataViewType,
    series: DataSerie<number, Date>[],
    benchmarks: DataSeries<number>,
    benchmarkData: BenchmarkData,
  ): void {
    series.forEach((serie) => {
      const kpiItem: SummaryCardKPIChartItem = {
        label: '',
        max: this.getMax(series.map((x) => x.values[0].value)),
        min: this.getMin(series.map((x) => x.values[0].value)),
      };

      kpiItem.threshold = undefined;
      if (
        benchmarks !== null &&
        benchmarks !== undefined &&
        benchmarks.series[kpi] !== undefined &&
        benchmarkData !== null &&
        benchmarkData !== undefined &&
        (this.benchmarkSettings.selectedNonDetailedBenchmarkType ===
          BenchmarkTypes.CustomerAverageSelectedPeriod ||
          benchmarkData.flocksUsed >= MIN_FLOCKS_FOR_BENCHMARK)
      ) {
        kpiItem.threshold = benchmarkData.benchmarks.find(
          (x) => x.parameter.code === serie.parameterCode,
        )?.value;

        if (kpiItem.threshold > kpiItem.max) {
          kpiItem.max = kpiItem.threshold;
        }
        if (kpiItem.threshold < kpiItem.min) {
          kpiItem.min = kpiItem.threshold;
        }
      }

      if (this.benchmarkSettings.selectedNonDetailedBenchmarkType === BenchmarkTypes.None) {
        kpiItem.threshold = null;
      }

      kpiItem.value = serie.values[0].value;
      kpiItem.unit = dataSeriesParameter.unit;
      kpiItem.prefixUnit = dataSeriesParameter.properties['PrefixUnit'] as unknown as boolean;
      kpiItem.numberOfDecimals = dataSeriesParameter.numberOfDecimals;

      switch (dataViewType) {
        case DataViewType.PerCycleBarn:
          kpiItem.label =
            (serie.properties['CycleNumber'] as unknown as string) +
            '-' +
            (serie.properties['BarnName'] as unknown as string);
          break;
        case DataViewType.PerCycle:
          kpiItem.label = `${serie.properties['CycleNumber'] as unknown as string} (${
            serie.properties['LocationName'] as unknown as string
          })`;
          break;
        case DataViewType.PerLocation:
          kpiItem.label = serie.properties['LocationName'] as unknown as string;
          break;
        case DataViewType.PerLocationBarn:
          kpiItem.label = `${serie.properties['BarnName'] as unknown as string} (${
            serie.properties['LocationName'] as unknown as string
          })`;
          break;
      }

      // Add item.
      this.kpiItems[kpi].push(kpiItem);
    });
  }

  sortTSDataByLocation(a: DataSerie<number, Date>, b: DataSerie<number, Date>) {
    const aLocation = a.properties['LocationName'] as unknown as string;
    const bLocation = b.properties['LocationName'] as unknown as string;

    if (aLocation < bLocation) {
      return -1;
    }
    if (aLocation > bLocation) {
      return 1;
    }
    return 0;
  }

  sortTSDataByCycle(a: DataSerie<number, Date>, b: DataSerie<number, Date>) {
    const aCycle = a.properties['CycleNumber'] as unknown as number;
    const bCycle = b.properties['CycleNumber'] as unknown as number;

    if (aCycle > bCycle) {
      return -1;
    }
    if (aCycle < bCycle) {
      return 1;
    }
    return 0;
  }

  sortTSDataByBarn(a: DataSerie<number, Date>, b: DataSerie<number, Date>) {
    const aBarn = a.properties['BarnId'] as unknown as number;
    const bBarn = b.properties['BarnId'] as unknown as number;

    if (aBarn > bBarn) {
      return -1;
    }
    if (aBarn < bBarn) {
      return 1;
    }
    return 0;
  }

  translateUnitOfMeasure(unit: string): string {
    const prefix = 'unitOfMeasure.';
    const translatedValue = this.localeService.translate(prefix + unit);
    if (translatedValue.includes(prefix)) {
      return unit;
    }
    return translatedValue;
  }

  benchmarkTitle$: Observable<string> = combineLatest([
    this.benchmarkSettings$,
    this.benchmarkLastWeeksData$,
    this.benchmarkSelectedPeriodData$,
    this.benchmarkCustomerAverageSelectedPeriodData$,
  ]).pipe(
    skipWhile(
      ([settings, _benchmarksLastWeeks, _benchmarksSelectedPeriod, _benchmarksCustomerAverage]) =>
        settings === undefined,
    ),
    map(([settings, benchmarksLastWeeks, benchmarksSelectedPeriod, benchmarksCustomerAverage]) => {
      if (settings.selectedNonDetailedBenchmarkType === BenchmarkTypes.LastWeeks) {
        return `${this.tenantTranslation.translate('farmer-insights.benchmark-title')}
        ${this.localeService.translate('farmer-insights.benchmark-last-weeks')} (${
          benchmarksLastWeeks?.flocksUsed
        } ${this.localeService.translate('flocks')})`;
      } else if (settings.selectedNonDetailedBenchmarkType === BenchmarkTypes.SelectedPeriod) {
        return `${this.tenantTranslation.translate('farmer-insights.benchmark-title')}
        ${this.localeService.translate('farmer-insights.benchmark-selected-period')}
        (${benchmarksSelectedPeriod?.flocksUsed} ${this.localeService.translate('flocks')})`;
      } else if (
        settings.selectedNonDetailedBenchmarkType === BenchmarkTypes.CustomerAverageSelectedPeriod
      ) {
        return `${this.localeService.translate('farmer-insights.customer-average-selected-period')}
        (${benchmarksCustomerAverage?.flocksUsed} ${this.localeService.translate('flocks')})`;
      }
      return '';
    }),
  );
}
